'use client';

import { createContext, type ReactNode, useContext, useMemo } from 'react';

import type { PropertyDetailListing } from '@/src/components/features/property/types';
import { useTourAction } from './useTourAction';

type HostedTourAction = {
  openTour: () => void;
  openTourReschedule: () => void;
  isTourReady: boolean;
};

type TourActionContextValue = {
  hostListing: PropertyDetailListing | null | undefined;
  openTourFrom: (triggerLocation: string) => void;
  openTourReschedule: () => void;
  isTourReady: boolean;
};

const TourActionContext = createContext<TourActionContextValue | null>(null);

/**
 * One tour wizard for everything under it that is showing the host's listing.
 *
 * `useTourAction` returns an overlay each caller has to render, so every caller holding its own
 * instance is another wizard that can mount on top of an open one. `book_tour` is what made that
 * reachable: an agent opens the wizard with no click, where a human click would have landed on
 * the open modal's backdrop and dismissed it instead. See docs/HP-7461/webmcp-actions.md.
 */
export function TourActionHost({ listing, children }: { listing?: PropertyDetailListing | null; children: ReactNode }) {
  const { openTourFrom, openTourReschedule, tourOverlay, isTourReady } = useTourAction(listing);
  const value = useMemo<TourActionContextValue>(
    () => ({ hostListing: listing, openTourFrom, openTourReschedule, isTourReady }),
    [isTourReady, listing, openTourFrom, openTourReschedule],
  );

  return (
    <TourActionContext.Provider value={value}>
      {children}
      {tourOverlay}
    </TourActionContext.Provider>
  );
}

/**
 * The shared wizard, attributed to `triggerLocation`. Null when there is no host, or when the
 * caller is showing a different listing than the host is — a unit inside a building page, say.
 * Either way the caller falls back to its own `useTourAction`, which is what keeps `ListingPage`,
 * `MediaCenter` and listing cards working as they always did.
 *
 * `listing` is required rather than optional so a caller cannot skip the comparison and silently
 * book a tour against the host's listing instead of its own.
 *
 * The trigger location binds here rather than per call, so the `openTour` this returns is
 * `() => void` and stays safe to hand straight to a DOM `onClick`.
 */
export function useOptionalHostedTourAction(
  triggerLocation: string,
  listing: PropertyDetailListing | null | undefined,
): HostedTourAction | null {
  const context = useContext(TourActionContext);
  const hostListing = context?.hostListing;
  // Identity first, since the normal case is one listing threaded down from the page. The alias
  // is the fallback for a re-fetched copy of that same listing.
  const matchesHost =
    !!context &&
    (listing === hostListing || (!!listing?.aliasEncoded && listing.aliasEncoded === hostListing?.aliasEncoded));
  const openTourFrom = context?.openTourFrom;
  const openTourReschedule = context?.openTourReschedule;
  const isTourReady = context?.isTourReady;

  return useMemo(
    () =>
      matchesHost && openTourFrom && openTourReschedule
        ? {
            openTour: () => openTourFrom(triggerLocation),
            openTourReschedule,
            isTourReady: isTourReady ?? false,
          }
        : null,
    [isTourReady, matchesHost, openTourFrom, openTourReschedule, triggerLocation],
  );
}

/** As above, for a caller that only ever renders inside a host, showing the host's listing. */
export function useHostedTourAction(
  triggerLocation: string,
  listing: PropertyDetailListing | null | undefined,
): HostedTourAction {
  const hosted = useOptionalHostedTourAction(triggerLocation, listing);

  if (!hosted) {
    throw new Error('useHostedTourAction must be used inside a TourActionHost showing the same listing');
  }

  return hosted;
}
